import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Check, RefreshCw, AlertCircle, Sparkles, Volume2, ShieldCheck, ChevronRight } from 'lucide-react';

const STEPS = [
  {
    id: 'front',
    label: 'Paso 1: Mirando al Frente',
    hint: 'Centra tu rostro dentro del marco guía',
    targetPitch: 'frente',
    guidance: 'Mantén la cabeza derecha y mira a la cámara'
  },
  {
    id: 'left',
    label: 'Paso 2: Perfil Izquierdo (45°)',
    hint: 'Gira lentamente tu cabeza hacia la izquierda',
    targetPitch: 'izquierda',
    guidance: 'Gira suavemente la cabeza hacia la izquierda'
  },
  {
    id: 'right',
    label: 'Paso 3: Perfil Derecho (45°)',
    hint: 'Gira lentamente tu cabeza hacia la derecha',
    targetPitch: 'derecha',
    guidance: 'Gira suavemente la cabeza hacia la derecha'
  },
  {
    id: 'up',
    label: 'Paso 4: Mentón Elevado',
    hint: 'Inclina suavemente la barbilla hacia arriba',
    targetPitch: 'arriba',
    guidance: 'Inclina un poco la barbilla hacia arriba'
  }
];

export function MobileFaceScannerView({ sessionId, avatarName = 'Mi Avatar' }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Biometric Analysis States
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceScore, setFaceScore] = useState(0); // 0 - 100
  const [biometricFeedback, setBiometricFeedback] = useState('Buscando rostro...');
  const [feedbackColor, setFeedbackColor] = useState('#94a3b8');
  const [detectorReady, setDetectorReady] = useState(false);
  const [lastCapturePreview, setLastCapturePreview] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animLoopRef = useRef(null);
  const detectorRef = useRef(null);
  const steadyTimeRef = useRef(0);
  const isCapturingRef = useRef(false);

  // Sintetizador de sonido biométrico (Web Audio API)
  const playBeep = useCallback((freq = 880, type = 'sine', duration = 0.15) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start();
      osc.stop(audioCtx.currentTime + duration);
    } catch (_) {}
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsCameraActive(true);
        };
      }
    } catch (err) {
      console.error('Error accediendo a la cámara en móvil:', err);
      setCameraError('Por favor autoriza el permiso de cámara frontal para calibrar tu rostro.');
      setIsCameraActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (animLoopRef.current) {
      cancelAnimationFrame(animLoopRef.current);
    }
    setIsCameraActive(false);
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const captureCurrentFrame = useCallback(() => {
    if (!videoRef.current || isCapturingRef.current) return;
    isCapturingRef.current = true;

    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 960;
    const ctx = canvas.getContext('2d');

    // Centrado de alta definición
    const vWidth = videoRef.current.videoWidth || 640;
    const vHeight = videoRef.current.videoHeight || 480;
    ctx.drawImage(videoRef.current, 0, 0, vWidth, vHeight, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    playBeep(1200, 'sine', 0.25); // Beep agudo de confirmación

    setLastCapturePreview(dataUrl);
    setTimeout(() => setLastCapturePreview(null), 1200);

    setCapturedFrames((prev) => {
      const updated = [...prev, { step: STEPS[currentStepIndex].id, dataUrl }];
      return updated;
    });

    setCountdown(null);
    steadyTimeRef.current = 0;

    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }

    setTimeout(() => {
      isCapturingRef.current = false;
    }, 1500);
  }, [currentStepIndex, playBeep]);

  const triggerManualCountdown = useCallback(() => {
    if (countdown !== null || isCapturingRef.current) return;
    setCountdown(3);
    playBeep(650, 'sine', 0.1);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          captureCurrentFrame();
          return null;
        }
        playBeep(750, 'sine', 0.1);
        return prev - 1;
      });
    }, 1000);
  }, [countdown, captureCurrentFrame, playBeep]);

  // Analizador Biométrico Facial Ultra-Robusto (Skin Hue, Bounding Box y Relación de Aspecto)
  useEffect(() => {
    // Inicializar detector
    const init = async () => {
      if (window.FaceDetector) {
        try {
          detectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        } catch (_) {}
      }
      setDetectorReady(true);
    };
    init();
  }, []);

  const analyzeVideoFrameBiometrics = useCallback(() => {
    if (!videoRef.current || videoRef.current.readyState < 2) return null;
    const vid = videoRef.current;
    const vW = vid.videoWidth || 640;
    const vH = vid.videoHeight || 480;

    // Usar un canvas offscreen pequeño para análisis rápido a 60fps sin laguear
    const offCanvas = document.createElement('canvas');
    offCanvas.width = 160;
    offCanvas.height = 120;
    const offCtx = offCanvas.getContext('2d', { willReadFrequently: true });
    offCtx.drawImage(vid, 0, 0, 160, 120);

    const imgData = offCtx.getImageData(0, 0, 160, 120);
    const data = imgData.data;

    let totalSkinPixels = 0;
    let sumX = 0;
    let sumY = 0;
    let minX = 160, maxX = 0, minY = 120, maxY = 0;
    let leftSkin = 0;
    let rightSkin = 0;

    for (let y = 0; y < 120; y += 2) {
      for (let x = 0; x < 160; x += 2) {
        const idx = (y * 160 + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];

        // Detección de tono de piel universal (YCbCr / RGB ratio invariant)
        const isSkin = (r > 60 && g > 40 && b > 20) &&
                       (r > g && r > b) &&
                       (Math.abs(r - g) > 15) &&
                       (r - b > 15);

        if (isSkin) {
          totalSkinPixels++;
          sumX += x;
          sumY += y;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;

          if (x < 80) leftSkin++;
          else rightSkin++;
        }
      }
    }

    const skinRatio = totalSkinPixels / (80 * 60); // Sobre 4800 puntos muestreados
    if (skinRatio < 0.05) {
      return { detected: false, reason: 'searching' };
    }

    const avgX = (sumX / totalSkinPixels) / 160; // 0.0 - 1.0
    const avgY = (sumY / totalSkinPixels) / 120; // 0.0 - 1.0
    const boxW = (maxX - minX) / 160;
    const boxH = (maxY - minY) / 120;

    // Cálculo de yaw / giro hacia izquierda o derecha
    // En video frontal invertido con scaleX(-1), rotar la cabeza hacia la izquierda física
    // desplaza el peso de la piel visible
    const yawBalance = (rightSkin - leftSkin) / (totalSkinPixels || 1); // -1 (izq) a +1 (der)

    return {
      detected: true,
      skinRatio,
      boxW,
      boxH,
      avgX,
      avgY,
      yawBalance
    };
  }, []);

  // Loop de Biometría Inteligente y Auto-Disparador
  useEffect(() => {
    if (!isCameraActive || isComplete || !videoRef.current) return;

    let isScanning = true;
    let lastScanTime = 0;

    const scanFaceFrame = async (timestamp) => {
      if (!isScanning) return;

      if (timestamp - lastScanTime > 120 && videoRef.current && videoRef.current.readyState >= 2) {
        lastScanTime = timestamp;

        try {
          const step = STEPS[currentStepIndex];
          let faceData = null;

          // Intento primario con API nativa de alta velocidad si existe
          if (detectorRef.current && typeof detectorRef.current.detect === 'function') {
            try {
              const faces = await detectorRef.current.detect(videoRef.current);
              if (faces && faces.length > 0) {
                const b = faces[0].boundingBox;
                const vW = videoRef.current.videoWidth || 640;
                const vH = videoRef.current.videoHeight || 480;
                faceData = {
                  detected: true,
                  skinRatio: (b.width * b.height) / (vW * vH),
                  boxW: b.width / vW,
                  boxH: b.height / vH,
                  avgX: (b.x + b.width / 2) / vW,
                  avgY: (b.y + b.height / 2) / vH,
                  yawBalance: 0
                };
              }
            } catch (_) {}
          }

          // Si no hay API nativa o falló, usar analizador de masa biométrica offscreen
          if (!faceData || !faceData.detected) {
            faceData = analyzeVideoFrameBiometrics();
          }

          if (!faceData || !faceData.detected) {
            setBiometricFeedback('Buscando rostro en el marco...');
            setFeedbackColor('#94a3b8');
            setFaceDetected(false);
            steadyTimeRef.current = 0;
          } else {
            setFaceDetected(true);

            // Validar distancia con rangos calibrados para smartphones (15% a 65%)
            if (faceData.skinRatio < 0.12 && faceData.boxH < 0.35) {
              setBiometricFeedback('Acércate un poco más');
              setFeedbackColor('#f59e0b');
              steadyTimeRef.current = 0;
            } else if (faceData.skinRatio > 0.65 || faceData.boxH > 0.85) {
              setBiometricFeedback('Aléjate un poco');
              setFeedbackColor('#f59e0b');
              steadyTimeRef.current = 0;
            } else if (Math.abs(faceData.avgX - 0.5) > 0.22) {
              setBiometricFeedback('Centra tu rostro dentro del marco');
              setFeedbackColor('#f59e0b');
              steadyTimeRef.current = 0;
            } else {
              // Encuadre y distancia correctos. Ahora validar el ángulo del paso actual
              let angleOk = true;
              let angleHint = '';

              if (step.id === 'front') {
                if (Math.abs(faceData.yawBalance) > 0.32) {
                  angleOk = false;
                  angleHint = 'Mira directo al centro de la cámara';
                }
              } else if (step.id === 'left') {
                // Requiere giro a la izquierda
                if (faceData.yawBalance < 0.08) {
                  angleOk = false;
                  angleHint = 'Gira tu cabeza hacia la izquierda (45°)';
                }
              } else if (step.id === 'right') {
                // Requiere giro a la derecha
                if (faceData.yawBalance > -0.08) {
                  angleOk = false;
                  angleHint = 'Gira tu cabeza hacia la derecha (45°)';
                }
              } else if (step.id === 'up') {
                if (faceData.avgY > 0.55) {
                  angleOk = false;
                  angleHint = 'Eleva ligeramente tu barbilla';
                }
              }

              if (!angleOk) {
                setBiometricFeedback(angleHint);
                setFeedbackColor('#38bdf8'); // Azul indicativo de rotación
                steadyTimeRef.current = 0;
              } else {
                // ¡Ángulo y distancia perfectos!
                setBiometricFeedback(`¡Perfecto! Mantén la posición (${step.targetPitch})`);
                setFeedbackColor('#10b981'); // Verde esmeralda de confirmación
                setFaceScore(98);

                steadyTimeRef.current += 1;

                // Si se mantiene estable durante 5 ciclos (~0.7 segundos), dispara de forma inteligente y automática
                if (steadyTimeRef.current >= 5 && countdown === null && !isCapturingRef.current && capturedFrames.length < STEPS.length) {
                  steadyTimeRef.current = 0;
                  triggerManualCountdown();
                }
              }
            }
          }
        } catch (_) {}
      }

      animLoopRef.current = requestAnimationFrame(scanFaceFrame);
    };

    animLoopRef.current = requestAnimationFrame(scanFaceFrame);
    return () => {
      isScanning = false;
      if (animLoopRef.current) cancelAnimationFrame(animLoopRef.current);
    };
  }, [isCameraActive, isComplete, currentStepIndex, countdown, capturedFrames.length, analyzeVideoFrameBiometrics]);

  const handleSendToDesktop = async () => {
    setIsSyncing(true);
    try {
      const angles = capturedFrames.map((f) => f.dataUrl);
      const res = await fetch('/api/scan/session/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || 'default_session',
          angles,
          avatarName
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setIsComplete(true);
        stopCamera();
      } else {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('Error sincronizando con la computadora:', err);
      // Si la red falla o está en modo offline, guardar en localStorage como respaldo
      try {
        localStorage.setItem(`offline_scan_${sessionId}`, JSON.stringify(capturedFrames.map((f) => f.dataUrl)));
        setIsComplete(true);
        stopCamera();
      } catch (_) {
        alert('Error al enviar las tomas. Por favor verifica tu conexión y presiona nuevamente.');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRetakeAll = () => {
    setCapturedFrames([]);
    setCurrentStepIndex(0);
    setCountdown(null);
    steadyTimeRef.current = 0;
    startCamera();
  };

  if (isComplete) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        background: '#090a0f',
        color: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#10b981',
          marginBottom: '20px',
          boxShadow: '0 0 35px rgba(16,185,129,0.4)',
          border: '2px solid #10b981'
        }}>
          <Check size={44} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 8px 0', color: '#10b981' }}>
          ¡Rostro Guardado!
        </h2>
        <p style={{ fontSize: '14px', color: '#94a3b8', maxWidth: '320px', lineHeight: '1.6' }}>
          Los 4 ángulos multi-perspectiva fueron recibidos por tu computadora. Puedes cerrar esta ventana y continuar en tu pantalla principal.
        </p>
      </div>
    );
  }

  const currentStep = STEPS[currentStepIndex] || STEPS[0];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#090a0f',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif',
      overflow: 'hidden',
      zIndex: 9999
    }}>
      {/* Barra Superior con Estado del Paso */}
      <div style={{
        padding: '14px 16px',
        background: 'rgba(15, 17, 24, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #a855f7, #6366f1)',
            padding: '6px',
            borderRadius: '8px',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Camera size={16} />
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 800 }}>Escáner Facial Biométrico</div>
            <div style={{ fontSize: '10px', color: '#94a3b8' }}>Avatar: {avatarName}</div>
          </div>
        </div>

        <div style={{
          fontSize: '11px',
          color: '#c084fc',
          background: 'rgba(168,85,247,0.18)',
          border: '1px solid rgba(192,132,252,0.4)',
          padding: '4px 10px',
          borderRadius: '14px',
          fontWeight: 800
        }}>
          Toma {Math.min(capturedFrames.length + 1, 4)} de 4
        </div>
      </div>

      {/* Viewport de Cámara Frontal con Óvalo Biométrico */}
      <div style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000'
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)'
          }}
        />

        {/* Óvalo Guía Inteligente con feedback dinámico de color */}
        <div style={{
          position: 'relative',
          width: '240px',
          height: '310px',
          borderRadius: '50%',
          border: countdown !== null
            ? '3.5px solid #ff9101'
            : faceDetected
            ? `3.5px solid ${feedbackColor}`
            : '3.5px dashed rgba(168, 85, 247, 0.6)',
          boxShadow: countdown !== null
            ? '0 0 0 9999px rgba(0, 0, 0, 0.65), 0 0 40px rgba(255,145,1,0.7)'
            : `0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 30px ${feedbackColor}55`,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease'
        }}>
          {countdown !== null ? (
            <div style={{
              fontSize: '88px',
              fontWeight: 900,
              color: '#ff9101',
              textShadow: '0 0 35px rgba(255,145,1,0.95)',
              animation: 'pulse 1s infinite'
            }}>
              {countdown}
            </div>
          ) : (
            <div style={{
              width: '80%',
              height: '2px',
              background: `linear-gradient(90deg, transparent, ${feedbackColor}, transparent)`,
              boxShadow: `0 0 10px ${feedbackColor}`,
              animation: 'scanLine 2s infinite ease-in-out'
            }} />
          )}

          {/* Guías biométricas superior, inferior y laterales */}
          <div style={{ position: 'absolute', top: '-10px', width: '36px', height: '3px', background: feedbackColor, borderRadius: '2px' }} />
          <div style={{ position: 'absolute', bottom: '-10px', width: '36px', height: '3px', background: feedbackColor, borderRadius: '2px' }} />
          <div style={{ position: 'absolute', left: '-10px', height: '36px', width: '3px', background: feedbackColor, borderRadius: '2px' }} />
          <div style={{ position: 'absolute', right: '-10px', height: '36px', width: '3px', background: feedbackColor, borderRadius: '2px' }} />
        </div>

        {/* Flash de Captura Exitosa (Muestra la foto capturada por 1 segundo) */}
        {lastCapturePreview && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 35,
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{
              width: '180px',
              height: '240px',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '3px solid #10b981',
              boxShadow: '0 0 40px rgba(16,185,129,0.5)',
              marginBottom: '12px'
            }}>
              <img src={lastCapturePreview} alt="Captura" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Check size={18} /> ¡Toma {currentStepIndex} Guardada!
            </div>
          </div>
        )}

        {/* Instrucción Flotante Superior */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '16px',
          right: '16px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{
            background: 'rgba(15, 17, 24, 0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(168,85,247,0.4)',
            borderRadius: '24px',
            padding: '10px 20px',
            textAlign: 'center',
            boxShadow: '0 8px 30px rgba(0,0,0,0.7)',
            maxWidth: '340px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 900, color: '#f8fafc' }}>
              {currentStep.label}
            </div>
            <div style={{ fontSize: '11px', color: '#c084fc', marginTop: '2px', fontWeight: 600 }}>
              {currentStep.hint}
            </div>
          </div>
        </div>

        {/* Notificación de Detección Biometría en Vivo (Acércate / Aléjate / Centrado) */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '16px',
          right: '16px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{
            background: 'rgba(10, 12, 18, 0.85)',
            backdropFilter: 'blur(8px)',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 700,
            color: feedbackColor,
            border: `1px solid ${feedbackColor}55`,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
          }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: feedbackColor }} />
            {biometricFeedback}
          </div>
        </div>
      </div>

      {/* Barra Inferior: Miniaturas con Preview y Botón Principal */}
      <div style={{
        background: '#0f1118',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        zIndex: 20
      }}>
        {/* Tira de 4 fotos capturadas con preview real */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          {STEPS.map((s, idx) => {
            const frame = capturedFrames[idx];
            const isCurrent = idx === currentStepIndex;
            return (
              <div
                key={s.id}
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '10px',
                  background: '#161926',
                  border: frame
                    ? '2.5px solid #10b981'
                    : isCurrent
                    ? '2.5px solid #a855f7'
                    : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: isCurrent ? '0 0 12px rgba(168,85,247,0.45)' : 'none',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                {frame ? (
                  <>
                    <img src={frame.dataUrl} alt={s.id} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      background: '#10b981',
                      borderRadius: '50%',
                      width: '14px',
                      height: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}>
                      <Check size={9} />
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 800 }}>
                    {idx + 1}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Botón de Captura Manual / Finalización */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {capturedFrames.length < STEPS.length ? (
            <button
              type="button"
              onClick={triggerManualCountdown}
              disabled={countdown !== null}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 800,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 6px 25px rgba(168, 85, 247, 0.45)',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Camera size={20} />
              {countdown !== null ? `Capturando en ${countdown}...` : `📸 Disparar (${currentStepIndex + 1}/4)`}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              <button
                type="button"
                onClick={handleRetakeAll}
                style={{
                  padding: '14px 16px',
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="Repetir escaneo"
              >
                <RefreshCw size={16} />
              </button>

              <button
                type="button"
                onClick={handleSendToDesktop}
                disabled={isSyncing}
                style={{
                  flex: 1,
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: 800,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 6px 25px rgba(16, 185, 129, 0.4)'
                }}
              >
                <Check size={20} />
                {isSyncing ? 'Transfiriendo a la PC...' : 'Guardar y Enviar a la PC'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
